import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Target,
  Compass,
  Sparkle,
  CheckCircle,
  ArrowLeft,
  Users,
  ChartLineUp,
  Handshake,
  Globe,
} from '@phosphor-icons/react';

const VALUES = [
  {
    icon: Users,
    title: 'الخبرة متعددة التخصصات',
    desc: 'فريق يجمع مستشارين تربويين، متخصصين في التشغيل، وقادة جودة في قطاع الطفولة المبكرة.',
  },
  {
    icon: Sparkle,
    title: 'الأدلة والممارسة',
    desc: 'كل خدمة مبنية على أبحاث حديثة وممارسات مهنية موثّقة، لا على اجتهادات نظرية.',
  },
  {
    icon: Globe,
    title: 'المعايير الدولية بسياق محلي',
    desc: 'نستند إلى أفضل الممارسات العالمية ونُواءمها مع البيئة النظامية والثقافية في المملكة.',
  },
  {
    icon: Target,
    title: 'الأثر الملموس',
    desc: 'نُصمّم كل تدخل ليُحدث فرقًا يُقاس على مستوى المنشأة والكادر والطفل.',
  },
  {
    icon: Handshake,
    title: 'الشراكة المهنية',
    desc: 'نبني قدرات الفريق مع كل مشروع، حتى يستمر الأثر بعد انتهاء التدخل.',
  },
];

const PILLARS = [
  'استشارات تربوية وإدارية مبنية على الأدلة والممارسات الدولية',
  'تأسيس وتشغيل منشآت الطفولة المبكرة وفق أعلى المعايير المهنية',
  'بناء منظومات الجودة والجاهزية للاعتماد الوطني والدولي',
  'تطوير الكوادر التربوية والقيادية بمناهج قائمة على اللعب والاستقصاء',
  'استقطاب الكفاءات التخصصية عبر بنك كفاءات مهني في الطفولة المبكرة',
  'أدوات وموارد مهنية قابلة للتطبيق الفوري داخل المنشأة',
];

const AboutPage = () => (
  <main
    data-testid="about-page"
    className="pt-24 pb-20 min-h-screen bg-[#FAFAF7]"
    dir="rtl"
  >
    {/* Hero */}
    <section className="bg-[#1F3D2B] text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-4 py-1.5 rounded-full border border-white/30 text-[#D8E0C9] text-xs sm:text-sm mb-6 tracking-wide">
            من نحن
          </span>
          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.15] font-['IBM_Plex_Sans_Arabic'] mb-6 tracking-tight"
            data-testid="about-hero-title"
          >
            بيت خبرة متخصص في قطاع الطفولة المبكرة
          </h1>
          <p className="text-base sm:text-lg text-[#D8E0C9] leading-10 max-w-3xl mb-8">
            <strong className="text-white font-semibold">Childhood Horizons — آفاق الطفولة</strong>
            {' '}
            بيت خبرة سعودي تقوده نخبة من الكفاءات الوطنية المتخصصة في قطاع
            الطفولة المبكرة، ينطلق من إيمان بأن الاستثمار في الطفولة هو
            استثمار في مستقبل الوطن. يقدم الاستشارات الإدارية وحلول الجودة
            والتطوير المؤسسي، ويعمل مع المنشآت والجهات على تطوير الهياكل
            والنماذج التشغيلية والسياسات والإجراءات ورفع كفاءة الأداء، وفق
            أفضل الممارسات الدولية وبما يتوافق مع السياق المهني في المملكة.
            نقدم حلولًا عملية قابلة للتطبيق، مبنية على الأدلة والخبرة
            الميدانية، ونسهم من خلالها في بناء قطاع طفولة مبكرة أكثر جودة
            واستدامة.
          </p>
          {/* Motto */}
          <div className="inline-block border-s-4 border-[#D8E0C9]/60 ps-4 py-1" data-testid="about-motto">
            <p className="text-[#D8E0C9] italic text-sm sm:text-base tracking-wide font-light">
              Show, Don&apos;t Tell. We Create Impact.
            </p>
          </div>
        </motion.div>
      </div>
    </section>

    {/* Mission / Vision */}
    <section className="py-16 sm:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-2 gap-6">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-white border border-[#E6E0D6] rounded-[24px] p-8 sm:p-10"
            data-testid="about-mission"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#F5F2EC] flex items-center justify-center mb-5">
              <Target size={24} weight="duotone" className="text-[#1F3D2B]" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
              رسالتنا
            </h2>
            <p className="text-[#3D512D] leading-9 text-sm sm:text-base">
              تمكين منشآت الطفولة المبكرة من رفع جودة ممارساتها التربوية
              والتشغيلية عبر حلول مبنية على الأدلة المهنية، ومواءمة المعايير
              الدولية مع السياق المحلي، بما يُحدث أثرًا حقيقيًا في تجربة الطفل
              والأسرة.
            </p>
          </motion.article>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white border border-[#E6E0D6] rounded-[24px] p-8 sm:p-10"
            data-testid="about-vision"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#F5F2EC] flex items-center justify-center mb-5">
              <Compass size={24} weight="duotone" className="text-[#1F3D2B]" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
              رؤيتنا
            </h2>
            <p className="text-[#3D512D] leading-9 text-sm sm:text-base">
              أن نكون المرجع المهني الأول في قطاع الطفولة المبكرة إقليميًا،
              وشريكًا استراتيجيًا للمنشآت والجهات في بناء بيئات تعلّم عالية
              الجودة تستند إلى أحدث الممارسات الدولية.
            </p>
          </motion.article>
        </div>
      </div>
    </section>

    {/* What we do */}
    <section className="py-16 sm:py-20 bg-[#F5F2EC]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <span className="inline-block px-4 py-1.5 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-xs sm:text-sm mb-4">
            نطاق عملنا
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
            ماذا نقدّم
          </h2>
          <p className="text-[#6B7565] max-w-2xl mx-auto leading-8 text-sm sm:text-base">
            ست ركائز عملية تُشكّل مجال تدخّلنا في قطاع الطفولة المبكرة.
          </p>
        </motion.div>
        <ul className="grid sm:grid-cols-2 gap-3">
          {PILLARS.map((p, i) => (
            <motion.li
              key={p}
              initial={{ opacity: 0, x: 14 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04, duration: 0.4 }}
              className="bg-white border border-[#E6E0D6] rounded-2xl p-5 flex items-start gap-3 text-sm text-[#3D512D] leading-8"
            >
              <CheckCircle
                size={18}
                weight="fill"
                className="text-[#4A641F] mt-0.5 shrink-0"
              />
              <span>{p}</span>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>

    {/* Values */}
    <section className="py-16 sm:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-1.5 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-xs sm:text-sm mb-4">
            قيمنا
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
            القيم التي نعمل من خلالها
          </h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {VALUES.map((v, i) => {
            const Icon = v.icon;
            return (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                className="bg-white border border-[#E6E0D6] rounded-2xl p-6"
              >
                <div className="w-11 h-11 rounded-xl bg-[#F5F2EC] flex items-center justify-center mb-3">
                  <Icon size={22} weight="duotone" className="text-[#4A641F]" />
                </div>
                <h3 className="text-base font-bold text-[#1F3D2B] mb-2 font-['IBM_Plex_Sans_Arabic']">
                  {v.title}
                </h3>
                <p className="text-sm text-[#6B7565] leading-7">{v.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-16 sm:py-20 bg-[#1F3D2B]">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <ChartLineUp size={36} weight="duotone" className="text-[#D8E0C9] mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 font-['IBM_Plex_Sans_Arabic']">
            مستعدّون للعمل معًا
          </h2>
          <p className="text-[#D8E0C9] leading-9 text-sm sm:text-base mb-7">
            تواصلوا معنا لمناقشة احتياج منشأتكم وبناء خطة عمل مخصّصة.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/quotation"
              data-testid="about-cta-quotation"
              className="bg-white text-[#1F3D2B] hover:bg-[#F5F2EC] rounded-xl px-7 py-3.5 text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
            >
              اطلب عرض سعر
              <ArrowLeft size={16} className="rtl-flip" />
            </Link>
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="border border-white/40 text-white hover:bg-white/10 rounded-xl px-7 py-3.5 text-sm font-medium inline-flex items-center justify-center transition-colors"
            >
              تواصل معنا
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  </main>
);

export default AboutPage;
